import { apiRoutes } from "../services/apiRoutes";
import { DateFormater } from "../services/DateFormater";
import * as ReactDOMServer from 'react-dom/server';
import { htmlEncode } from "../services/Utils";

// Encode the free-text document fields for display. htmlEncode runs String(value), so a null has to
// be short-circuited to "" here or the grid renders the literal text "null".
const encodeDocumentFields = (doc) => {
    doc.documentName = htmlEncode(doc.documentName);
    doc.documentManufacturer = htmlEncode(doc.documentManufacturer);
    doc.textValue1 = doc.textValue1 == null ? "" : htmlEncode(doc.textValue1);
    doc.textValue2 = doc.textValue2 == null ? "" : htmlEncode(doc.textValue2);
    doc.textValue3 = doc.textValue3 == null ? "" : htmlEncode(doc.textValue3);
    doc.formatId = doc.formatId == null ? "" : htmlEncode(doc.formatId);
    doc.documentFormat = doc.documentFormat == null ? "" : htmlEncode(doc.documentFormat);
    doc.documentSourceId = htmlEncode(doc.documentSourceId);
};

// Sorted, de-duplicated tag chips, pre-rendered to markup for the grid cell.
const buildDocumentTags = (doc) => {
    doc.documentTags.sort((a, b) => a.localeCompare(b));
    let docTagsUnique = [...new Set(doc.documentTags)];
    doc.documentTagsTextDisplay = ReactDOMServer.renderToStaticMarkup(<div className="gridRowTags">{docTagsUnique.map((x, index) =>
        <div className="sphera-chip" key={index + "docTag"}>
            <div className="k-chip k-selected k-focus k-chip-md k-rounded-md k-chip-solid k-chip-solid-base chip-purple s-chip">
                <span className="k-chip-content">
                    <span className="k-chip-label">{htmlEncode(x)}</span>
                </span>
            </div>
        </div>)}</div>);
    doc.documentTagsText = docTagsUnique.join(" ");
};

export const MaterialData = (props) => {
    const { adjustForTimezone, formatDate } = DateFormater();
    
    const phraseSectionInfo = 1;
    const phraseSectionIn = 3;
    const phraseSectionPh = 9;
    const phraseSectionTrans = 14;

    const sensitivityLevels = [
        {
            sensitivity: props.localizations.InternalProprietary,
            sensitivityId: 1
        },
        {
            sensitivity: props.localizations.Public,
            sensitivityId: 2
        },
    ];

    const statusTypes = [
        {
            status: props.localizations.Inactive,
            statusId: 0
        },
        {
            status: props.localizations.Active,
            statusId: 1
        },
        {
            status: props.localizations.Pending,
            statusId: 2
        },
        {
            status: props.localizations.Failed,
            statusId: 3
        },
        {
            status: props.localizations.Archived,
            statusId: 4
        }
    ];

    const base64ToArrayBuffer = (base64) => {
        var binaryString = window.atob(base64);
        var binaryLen = binaryString.length;
        var bytes = new Uint8Array(binaryLen);
        for (var i = 0; i < binaryLen; i++) {
            var ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    };

    const getCorrectLocalization = (synonyms, cultureInfoCode, IsChemicalName, IsMfgName) => {
        let synType = 2;
        if (IsChemicalName)
            synType = 3;
        if (IsMfgName)
            synType = 4;

        if (synonyms == null || synonyms.length === 0)
            return "";

        let localization = synonyms.find(x => x.cultureInfoCode === cultureInfoCode && x.synonymType === synType);
        if (localization != null)
            return localization.synonym;

        let languageSection = (cultureInfoCode.indexOf("-") !== -1) ? cultureInfoCode.substring(0, cultureInfoCode.indexOf("-") + 1) : cultureInfoCode;
        localization = synonyms.find(x => x.cultureInfoCode.indexOf(languageSection) !== -1 && x.synonymType === synType);
        if (localization != null)
            return localization.synonym;

        localization = synonyms.find(x => x.cultureInfoCode.indexOf("en-") !== -1 && x.synonymType === synType);
        if (localization != null)
            return localization.synonym;


        localization = synonyms.find(x => x.synonymType === synType);
        if (localization != null)
            return localization.synonym;

        if (IsMfgName)
            return null;

        return "";
    }

    // Strip the UTC offset and re-express each stamp in the customer's timezone. documentDate1 is
    // date-only unless a time component is present, and an unset date collapses to "".
    const applyDocumentTimestamps = (doc) => {
        doc.revisionDateDisplay = new Date(new Date(doc.revisionDate).toDateString());
        doc.revisionDate = new Date(new Date(doc.revisionDate).toDateString());

        doc.timestampCreateDisplay = adjustForTimezone(doc.timestampCreate.substring(0, doc.timestampCreate.indexOf("+")), props.customer.TimezoneId);
        doc.timestampCreate = adjustForTimezone(doc.timestampCreate.substring(0, doc.timestampCreate.indexOf("+")), props.customer.TimezoneId);
        if (doc.timestampModified != null) {
            doc.timestampModifiedDisplay = adjustForTimezone(doc.timestampModified.substring(0, doc.timestampModified.indexOf("+")), props.customer.TimezoneId);
            doc.timestampModified = adjustForTimezone(doc.timestampModified.substring(0, doc.timestampModified.indexOf("+")), props.customer.TimezoneId);
        }

        if (doc.documentDate1 != null && doc.documentDate1 !== '0001-01-01T00:00:00') {
            let length = (doc.documentDate1.indexOf("+") > 0) ? doc.documentDate1.indexOf("+") : doc.documentDate1.length;
            let timeExists = doc.documentDate1.indexOf("00000") <= 0 && doc.documentDate1.indexOf("00:00:00") <= 0;
            doc.documentDate1Display = (timeExists) ? adjustForTimezone(doc.documentDate1.substring(0, length), 0) : new Date(new Date(doc.documentDate1.substring(0, length)).toDateString());
            doc.documentDate1 = (timeExists) ? adjustForTimezone(doc.documentDate1.substring(0, length), 0) : new Date(new Date(doc.documentDate1.substring(0, length)).toDateString());
        }
        else {
            doc.documentDate1 = "";
        }
    };

    const getMaterial = async (MaterialId, status) => {
        let phraseSections = null;
        let phraseSectionsResults = await props.execute("GET", `${apiRoutes.documents.getPhraseSections}`);
        if (phraseSectionsResults != null)
            phraseSections = phraseSectionsResults.sections;

        let json = await props.execute("GET", `${apiRoutes.materials.get(MaterialId, localStorage.getItem("languages"), props.searchArchive)}`);

        let newDocuments = json.documents;
        newDocuments.forEach((doc) => {
            doc.sensitivity = sensitivityLevels.find(x => x.sensitivityId === doc.sensitivity).sensitivity;
            doc.statusText = statusTypes.find(x => x.statusId === doc.status).status;

            doc.languagesText = doc.languages.map((x) => x.languageName + "-" + x.country).join(', ');

            if (doc.regulatoryJurisdiction == null || doc.regulatoryJurisdiction === "")
                doc.regulatoryJurisdiction = props.localizations.Global;

            applyDocumentTimestamps(doc);
            encodeDocumentFields(doc);
            buildDocumentTags(doc);
        });

        if (json.timestampCreate != null) {
            let timestampCreateTimezone = adjustForTimezone(json.timestampCreate.substring(0, json.timestampCreate.indexOf("+")), props.customer.TimezoneId);
            let formatedDate = formatDate(timestampCreateTimezone, props.customer.DateFormatId, true);
            json.timestamp_Create = formatedDate;
        }
        if (json.timestampModified != null) {
            let timestampModifiedTimezone = adjustForTimezone(json.timestampModified.substring(0, json.timestampModified.indexOf("+")), props.customer.TimezoneId);
            let formatedDate = formatDate(timestampModifiedTimezone, props.customer.DateFormatId, true);
            json.timestamp_Modified = formatedDate;
        }

        json.synonymsString = [...new Set(json.synonyms.filter(x => x.synonymType === 1).map((x) => x.synonym))].join(', ');
        if (json.synonymsString.length > 200)
            json.synonymsString = json.synonymsString.substring(0, 200) + "...";
        json.materialCodesText = json.materialCodes.join(', ');
        let synonyms = json.synonyms.filter(x => x.synonymType === 1);

        json.materialName = getCorrectLocalization(json.synonyms, props.languageValue.languageCode, false, false);
        json.chemicalName = getCorrectLocalization(json.synonyms, props.languageValue.languageCode, true, false);
        json.manufacturer = getCorrectLocalization(json.synonyms, props.languageValue.languageCode, false, true) ?? json.manufacturer;

        let tagsUnique = [...new Set(json.materialTags.map(x => x.tag))];
        json.materialTagsText = <div className="gridRowTags">{tagsUnique.map((y, index) =>
            <div className="sphera-chip" key={index + "matTag"} >
                <div className="k-chip k-selected k-focus k-chip-md k-rounded-md k-chip-solid k-chip-solid-base chip-purple s-chip">
                    <span className="k-chip-content">
                        <span className="k-chip-label">{y}</span>
                    </span>
                </div>
            </div>)}</div>;

        // Aggregated document-level columns for the Material Details tab: ellipsis-capped list
        // with the full list in a tooltip (rendered via dangerouslySetInnerHTML in MaterialDetails).
        ["documentFormats", "documentLocations", "documentTypes", "docText1s", "docText2s", "docText3s", "documentManufacturers"].forEach(f => {
            let listVals = [...new Set((json[f] || []).map(v => v.textValue).filter(Boolean))].sort((a, b) => a.localeCompare(b));
            let joined = listVals.join(", ");
            json[f + "Display"] = ReactDOMServer.renderToStaticMarkup(<div className="gridRowList" title={joined}>{joined}</div>);
            json[f + "Text"] = joined;
        });

        if (json.casNumber != null) {
            let length = json.casNumber.length;
            json.CasNumberString = json.casNumber;
            if (json.casNumber.indexOf("-") === -1 && json.casNumber.length > 0)
                json.CasNumberString = json.casNumber.slice(0, length - 3) + "-" + json.casNumber.slice(length - 3, length - 1) + "-" + json.casNumber.slice(length - 1);
        }

        let filteredDocs = newDocuments;
        if (!status)
            filteredDocs = filteredDocs.filter(x => x.status === 1 || x.status === 4);

        return { phraseSections, filteredDocs, materialData: json, newDocuments, synonyms }
    };

    const listToTree = (list) => {
        var map = {}, node, roots = [], i;

        for (i = 0; i < list.length; i += 1) {
            map[list[i].phraseSectionTypeId] = i;
            list[i].children = [];
        }

        for (i = 0; i < list.length; i += 1) {
            node = list[i];
            if (node.phraseSectionType.parentPhraseSectionTypeId !== null) {
                list[map[node.phraseSectionType.parentPhraseSectionTypeId]].children.push(node);
            } else {
                roots.push(node);
            }
        }
        return roots;
    }

    const getDocumentData = async (documentName, materialData, phraseSections) => {
        let docId = documentName.replace(".pdf", "");
        let document = materialData.documents.find(x => x.documentId === docId);
        let sections = [];
        let pictogramsJson, tree, link = null;
        let standardIngredients = [];
        let fullIngredients = [];
        if (document) {
            let pictogramIds = "";
            let sectionAdded = false;
            document.documentPhrases.forEach(x => {
                let section = phraseSections.find(y => y.phraseSectionId === x.phraseSectionId);
                let newSection = sections.find(y => y.phraseSectionId === section.phraseSectionId);
                if (section && !newSection) {
                    section.phrases = document.documentPhrases.filter(x => x.phraseSectionId === section.phraseSectionId);
                    section.value = section.phraseSectionId;
                    section.label = section.phraseSectionType.name;
                    section.show = true;
                    sections.push(section);
                }
            })
            do {
                sectionAdded = false;
                for (var i = 0; i < sections.length; i++) {
                    if (sections[i].phraseSectionType.parentPhraseSectionTypeId) {
                        let section = sections.find(y => y.phraseSectionTypeId === sections[i].phraseSectionType.parentPhraseSectionTypeId);
                        if (!section) {
                            let newSection = phraseSections.find(y => y.phraseSectionTypeId === sections[i].phraseSectionType.parentPhraseSectionTypeId);
                            newSection.phrases = document.documentPhrases.filter(x => x.phraseSectionId === newSection.phraseSectionId);
                            newSection.value = newSection.phraseSectionId;
                            newSection.label = newSection.phraseSectionType.name;
                            newSection.show = true;
                            sections.push(newSection);
                            sectionAdded = true;
                        }
                    }

                    for (var j = 0; j < sections[i].phrases.length; j++) {
                        if (sections[i].phrases[j].pictogramId != null)
                            pictogramIds += sections[i].phrases[j].pictogramId + ",";
                    }
                }
            }
            while (sectionAdded)

            pictogramsJson = await props.execute("GET", apiRoutes.documents.pictograms(pictogramIds.substring(0, pictogramIds.length - 1)));

            tree = listToTree(sections.sort((x, y) => x.sortOrder < y.sortOrder));

            if (document.formulations.length > 0) {
                if (document.formulations[0].formulationType === 0)
                    standardIngredients = document.formulations[0].ingredients;
                else
                    fullIngredients = document.formulations[0].ingredients;
            }
            if (document.formulations.length > 1) {
                if (document.formulations[1].formulationType === 0)
                    standardIngredients = document.formulations[1].ingredients;
                else
                    fullIngredients = document.formulations[1].ingredients;
            }

            document.transportation.forEach(x => {
                x.placards = "";
                x.hazards.forEach(y => {
                    x.placards += " " + y.class + ",";
                });

                x.placards = x.placards.substring(0, x.placards.length - 1);
            });

            phraseSections.forEach(x => {
                if (x.phraseSectionType.parentPhraseSectionTypeId == null &&
                    x.showInDetails && tree.find(y => y.phraseSectionTypeId === x.phraseSectionTypeId) == null) {
                    if (x.phraseSectionTypeId === phraseSectionPh ||
                        x.phraseSectionTypeId === phraseSectionInfo ||
                        x.phraseSectionTypeId === phraseSectionIn ||
                        x.phraseSectionTypeId === phraseSectionTrans) {
                        if (x.phraseSectionTypeId === phraseSectionPh &&
                            (document.documentProperties == null || (document.documentProperties.physicalProperties.length === 0 && document.documentProperties.hmisProperties.length === 0 && document.documentProperties.nfpaProperties.length === 0)))
                            return;
                        if (x.phraseSectionTypeId === phraseSectionIn && (fullIngredients.length === 0 && standardIngredients.length === 0))
                            return;
                        if (x.phraseSectionTypeId === phraseSectionTrans && (document.transportation.length === 0))
                            return;
                    }
                    else
                        return;
                    x.children = [];
                    x.show = true;
                    x.label = props.localizations[x.phraseSectionType.name] ?? x.phraseSectionType.name;
                    x.value = x.phraseSectionId;
                    tree.push(x);
                }
            });

            tree = tree.sort((a, b) => a.sortOrder - b.sortOrder).filter(x => x.showInDetails);
        }

        let json = await props.execute("GET", `${apiRoutes.documents.name}?fileName=${documentName}`);
        if (json.fileName.indexOf(".docx") > 0) {
            const link = document.createElement("a");
            link.href =
                "data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64," +
                json.fileData;
            link.download = json.documentName;
            link.click();
        }
        if (json.fileName.indexOf(".pdf") > 0) {
            var blob = {};
            var arrrayBuffer = base64ToArrayBuffer(json.fileData);
            blob = new Blob([arrrayBuffer], { type: "application/pdf" });
            link = window.URL.createObjectURL(blob);
        }

        return { pictograms: pictogramsJson.pictograms, standardIngredients, fullIngredients, document, tree, link };
    };

    const editTags = async (DocumentIds, tagsValue, IsDelete) => {
        let postData = { DocumentIds, DocumentTags: tagsValue, IsDelete };
        await props.execute("POST", apiRoutes.documents.editTags, postData)
    };

    return {
        getMaterial, getCorrectLocalization, getDocumentData, editTags
	};
}